// Pure helpers for the Action items panel (components/panel/ActionItems.tsx), kept out of
// the component file so it only exports components (react-refresh).
import type { SummaryContent } from '@scribe/core/types';

export type ActionItem = SummaryContent['action_items'][number];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Formats an ISO `YYYY-MM-DD` due date as "Jul 11"; the model may also return free text
 * ("next Friday"), which is shown verbatim. */
export function formatDue(due: string): string {
  if (!ISO_DATE.test(due)) return due;
  const d = new Date(due + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return due;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** `- [ ] item — owners (due X)` per line; owner/due parts omitted when absent. */
export function toMarkdownChecklist(items: ActionItem[]): string {
  return items
    .map((it) => {
      let line = `- [ ] ${it.item}`;
      if (it.owners.length > 0) line += ` — ${it.owners.join(', ')}`;
      if (it.due) line += ` (due ${it.due})`;
      return line;
    })
    .join('\n');
}

/** How long after transcription completes the panel keeps waiting (and polling) for the
 * summary artifact. The summary pass runs right after persist, in the persist Lambda (15 min
 * hard cap) or the bridge, so a summary that hasn't landed by then is not coming: the pass
 * failed, was skipped, or the recording predates AI summaries. */
export const SUMMARY_WAIT_MS = 15 * 60_000;

/** Milliseconds left in the wait window. Anchored on `transcription_completed_at` when the
 * recording has a valid one; otherwise the whole window from `now` (the panel's first look). */
export function summaryWaitRemainingMs(
  transcriptionCompletedAt: string | null | undefined,
  now: number,
): number {
  const anchor = transcriptionCompletedAt ? Date.parse(transcriptionCompletedAt) : Number.NaN;
  if (Number.isNaN(anchor)) return SUMMARY_WAIT_MS;
  return Math.max(0, anchor + SUMMARY_WAIT_MS - now);
}
